import { followArtist, unfollowArtist } from 'api/artists'
import classNames from 'classnames'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { useUserProfile } from 'hooks'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'
import { ComponentProps, useCallback, useEffect, useState } from 'react'
import { PropsWithClassName } from 'types'
import { Artist } from 'utils/data'
import { getArtistUrl } from 'utils/urls'

const sizes = {
  sm: {
    container: 'space-x-2.5',
    imageLink: 'max-h-[3rem] min-h-[3rem] min-w-[3rem] max-w-[3rem]',
    imageSize: 48,
    artistName: 'text-sm',
    nationality: 'text-xs',
  },
  default: {
    container: 'space-x-4',
    imageLink: 'max-h-[4rem] min-h-[4rem] min-w-[4rem] max-w-[4rem]',
    imageSize: 64,
    artistName: 'text-base',
    nationality: 'text-sm',
  },
} as const

export type Props = {
  artist: Artist
  followId?: number
  buttonSize?: ComponentProps<typeof Button>['size']
  size?: keyof typeof sizes
} & PropsWithClassName

export const ArtistPreview = ({
  className,
  artist,
  followId: initialFollowId,
  buttonSize,
  size = 'default',
}: Props) => {
  const { t } = useTranslation()
  const { data: userProfile } = useUserProfile()

  const [isLoading, setIsLoading] = useState(false)
  const [followId, setFollowId] = useState(initialFollowId)

  const isFollowing = Boolean(followId)

  const artistHref = getArtistUrl(artist.name, artist.id)

  const onUnfollow = useCallback(async () => {
    if (isLoading || !followId) return

    try {
      setFollowId(undefined) // optimistic update
      setIsLoading(true)

      const { error } = await unfollowArtist(followId)

      if (error) throw new Error(error.message)
    } catch (err) {
      setFollowId(followId) // if request failed, revert to initial state
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, followId])

  const onFollow = useCallback(async () => {
    if (isLoading) return

    try {
      setFollowId(1) // placeholder for optimistic update
      setIsLoading(true)

      const { error, id } = await followArtist({
        artistId: artist.id,
        newHighestBidEmail: Boolean(userProfile?.followArtistNewHighestBid),
        newLowestAskEmail: Boolean(userProfile?.followArtistNewLowestAsk),
        newSaleEmail: Boolean(userProfile?.followArtistNewSale),
        newReleaseEmail: Boolean(userProfile?.followArtistNewRelease),
      })

      if (error) throw new Error(error.message)

      setFollowId(id) // set the actual id
    } catch (err) {
      setFollowId(undefined) // if the request failed, revert to initial state
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }, [artist, isLoading, userProfile])

  useEffect(() => {
    if (initialFollowId) {
      setFollowId(prev => {
        if (prev) return prev
        return initialFollowId
      })
    }
  }, [initialFollowId])

  const sizeClassNames = sizes[size]

  return (
    <div className={classNames('flex w-full items-center', className)}>
      <div
        className={classNames('flex items-center', sizeClassNames.container)}
      >
        <Link
          href={artistHref}
          className={classNames('flex', sizeClassNames.imageLink)}
        >
          {(artist.avatar?.url || artist.profile?.url) && (
            <Image
              src={(artist.avatar?.url || artist.profile?.url) as string}
              alt={artist.name}
              width={sizeClassNames.imageSize}
              height={sizeClassNames.imageSize}
              className='rounded-full object-cover object-center'
            />
          )}
        </Link>

        <div>
          <Link href={artistHref}>
            <h3
              className={classNames(
                'font-body font-medium tracking-normal',
                sizeClassNames.artistName
              )}
            >
              {artist.name}
            </h3>
          </Link>
          <h4
            className={classNames(
              'break-word font-body font-normal tracking-normal text-cool-600',
              sizeClassNames.nationality
            )}
          >
            {artist.nationalities.map(nat => nat.name).join(', ')}
            {artist.birthYear && <>, b. {artist.birthYear}</>}
          </h4>
        </div>
      </div>

      {isFollowing ? (
        <Button pill size={buttonSize} style='outline' onClick={onUnfollow}>
          <Icon name='check' />
          <span className='ml-2.5'>{t('common:following')}</span>
        </Button>
      ) : (
        <Button pill size={buttonSize} onClick={onFollow}>
          <Icon name='plus' />
          <span className='ml-2.5'>{t('common:follow')}</span>
        </Button>
      )}
    </div>
  )
}
